// 組織根節點的顯示（SPEC #vms-web-220「組織根節點」；VMS UX 評審第二輪 B2-04、B2-05）：
//   根節點不用資料裡的內部名（舊資料可能叫 lstw、跟底下的場域同名）——根節點本身寫「<組織名>（整個組織）」，
//   路徑一律從組織名起算（「LivingStyle › lstw › lab」）。後端（GET /v1/orgs/{org}/access）回的路徑第一段是根節點名，這裡換掉。

export const PATH_SEP = ' › ';

export interface SiteLite {
  site_id: string;
  name: string;
  parent_site_id?: string | null;
  status?: string;
}

/** 組織的根節點：parent 空的 site（多筆時取沒停用的第一筆，同後端 RootSiteOf 的順序近似）。 */
export function rootSite<S extends SiteLite>(sites: S[]): S | undefined {
  const roots = sites.filter((s) => !s.parent_site_id);
  return roots.find((s) => s.status !== 'archived') ?? roots[0];
}

/** 從根到這個節點的名稱（含自己；根節點換成組織名）。查不到 = null。 */
export function siteNamePath(sites: SiteLite[], id: string, orgName: string): string[] | null {
  const by = new Map(sites.map((s) => [s.site_id, s]));
  const out: string[] = [];
  let cur = by.get(id);
  for (let n = 0; cur && n < 64; n++) {
    out.unshift(cur.parent_site_id ? cur.name : orgName);
    if (!cur.parent_site_id) return out;
    cur = by.get(cur.parent_site_id);
  }
  return out.length ? out : null;
}

/**
 * 後端路徑（「lstw › lstw › lab」，第一段 = 根節點名）→ 從組織名起算（「LivingStyle › lstw › lab」）。
 * 路徑就是根節點本身 → wholeOrg（「LivingStyle（整個組織）」）。沒有根節點資料時原樣回。
 */
export function orgPath(path: string, root: { name: string } | { name: string }[] | undefined, orgName: string, wholeOrg: string): string {
  if (!path) return wholeOrg;
  const roots = Array.isArray(root) ? root : root ? [root] : [];
  // 根節點資料還沒到 / 讀不到（第三輪 B3-05：組織與場域 › 誰能存取仍顯示「lstw › lsdg › rd」）：後端的節點路徑一律從根節點起算，第一段換成組織名
  if (!roots.length) {
    const i = path.indexOf(PATH_SEP);
    return i < 0 ? wholeOrg : orgName + path.slice(i);
  }
  for (const r of roots) {
    if (path === r.name) return wholeOrg;
    if (path.startsWith(r.name + PATH_SEP)) return orgName + path.slice(r.name.length);
  }
  return path;
}

/**
 * 「誰能存取」的範圍欄：相對於選取節點的路徑（「lab」「rd › 會議室」）；就是這個節點 → here；
 * 不在它底下（從上層繼承的列）→ 從組織名起算的完整路徑。
 */
export function relativePath(full: string, nodeFull: string | null, here: string): string {
  if (!nodeFull) return full;
  if (full === nodeFull) return here;
  if (full.startsWith(nodeFull + PATH_SEP)) return full.slice(nodeFull.length + PATH_SEP.length);
  return full;
}
